import { LoadedProps, RouteLoadProps } from '@components/router';
import { StudentPage } from '@course/components/student-page';
import { defStudentRoute } from '@course/components/courses-app-router';
import { rpx } from 'client/lib/rpx-client';
import { useEffect, useState } from 'preact/hooks';
import { useIntl } from 'shared/intl/use-intl';
import dayjs from 'dayjs';
import { TodaysCheckinCard } from '@course/components/checkins/todays-checkin-card';
import { CheckinHistory } from '@course/components/checkins/checkin-history';
import { WeeklySummaryCard } from '@course/components/checkins/weekly-summary-card';
import { SummaryHistory } from '@course/components/checkins/summary-history';
import type { CheckinSummary } from 'server/dbx/checkin-summaries';
import type { CatchUpDay, Checkin } from 'server/dbx/checkins';
import { nextCheckinDay } from '@course/components/checkins/next-checkin-day';
import { isCheckinDayIso, isoDayOfWeekFromJsDay } from 'shared/checkin-days';
import { BtnPrimary } from '@components/buttons';
import { IcoCheckCircle, IcoPencilSquare, IcoPlusCircle } from '@components/icons';
import { URLS } from 'shared/urls';
import {
  loadTrackerGlance,
  showsTrackerGlance,
  TrackerGlance,
  trackerHref,
} from '@course/components/tracker/glance';

export const route = defStudentRoute({
  load,
  Page,
  authLevel: 'student',
});

function CatchUpDayChip({
  day,
  isSelected,
  onClick,
}: {
  day: CatchUpDay;
  isSelected: boolean;
  onClick: () => void;
}) {
  const intl = useIntl();
  const checkin = day.checkin;
  const label = dayjs(day.date).format('ddd M/D');
  const base =
    'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-colors';
  const stateClass = isSelected
    ? 'border-indigo-600 bg-indigo-50 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300'
    : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600';

  return (
    <button type="button" class={`${base} ${stateClass}`} onClick={onClick}>
      {checkin && !checkin.isDraft && (
        <IcoCheckCircle class="size-4 text-green-600 dark:text-green-400 shrink-0" />
      )}
      {checkin?.isDraft && (
        <IcoPencilSquare class="size-4 text-amber-500 dark:text-amber-400 shrink-0" />
      )}
      {!checkin && <IcoPlusCircle class="size-4 text-gray-400 shrink-0" />}
      <span>{label}</span>
      <span class="sr-only">
        {checkin && !checkin.isDraft && intl('submitted')}
        {checkin?.isDraft && intl('draft')}
        {!checkin && intl('not started')}
      </span>
    </button>
  );
}

function Page(props: LoadedProps<typeof load>) {
  const intl = useIntl();
  const { course, settings, localDate, isActiveDay } = props.state;
  const [today, setToday] = useState(props.state.today);
  const [catchUpDays, setCatchUpDays] = useState(props.state.catchUpDays);
  const [selectedDate, setSelectedDate] = useState<string | undefined>();
  const [checkins, setCheckins] = useState(props.state.checkins);
  const { feedback, loadedAt } = props.state;
  const { openSummary } = props.state;
  const [summaries, setSummaries] = useState(props.state.summaries);
  const [tracker, setTracker] = useState<TrackerGlance>();
  const summaryWeekStart = openSummary?.window.weekStart;

  useEffect(() => {
    if (!settings.checkinsEnabled || !showsTrackerGlance(course)) {
      return;
    }
    let isCurrent = true;
    loadTrackerGlance(course.id, summaryWeekStart).then((glance) => {
      if (isCurrent) {
        setTracker(glance);
      }
    });
    return () => {
      isCurrent = false;
    };
  }, [course.id, summaryWeekStart]);

  function onSummarySaved(summary: CheckinSummary) {
    if (summary.isDraft) {
      return;
    }
    setSummaries((x) => [summary, ...x.filter((s) => s.weekStart !== summary.weekStart)]);
  }

  // Replies and reactions count as seen as soon as this page shows them. The
  // New labels come from what was loaded, so they stay for this visit.
  useEffect(() => {
    if (!props.state.unseenFeedback.count) {
      return;
    }
    rpx.checkins.markCheckinFeedbackSeen({ courseId: course.id, upTo: loadedAt }).catch(() => {
      // Not worth interrupting the student: the labels show as new next time.
    });
  }, []);

  function onSaved(checkin: Checkin) {
    if (checkin.checkinDate === localDate) {
      setToday(checkin);
    }
    setCatchUpDays((days) =>
      days.map((d) => (d.date === checkin.checkinDate ? { ...d, checkin } : d)),
    );
    // History only ever shows submitted standups — a draft isn't "posted"
    // yet, so it stays out of this list (the catch-up chip above already
    // shows its draft state) until it's actually submitted.
    if (!checkin.isDraft) {
      setCheckins((x) => [checkin, ...x.filter((c) => c.checkinDate !== checkin.checkinDate)]);
    }
  }

  const beforeWindow = !!settings.startDate && localDate < settings.startDate;
  const afterWindow = !!settings.endDate && localDate > settings.endDate;
  // isActiveDay is false for guides and facilitators on any day, so it cannot
  // stand in for "today is not a standup day" — that would tell a facilitator
  // the next standup is Thursday while a student is posting one today.
  const isStandupDay = isCheckinDayIso(
    isoDayOfWeekFromJsDay(dayjs(localDate).day()),
    settings.days,
  );
  const nextDay = nextCheckinDay(localDate, settings.days, settings.endDate);
  // Coaches land here from the course menu, but they read standups on the
  // Students screen; this page's own history would only ever be empty for them.
  const isCoach = course.level === 'guide' || course.level === 'facilitator';
  const selectedDay = selectedDate ? catchUpDays.find((d) => d.date === selectedDate) : undefined;

  return (
    <StudentPage course={course} currentLink="checkins">
      <div class="max-w-3xl w-full mx-auto px-6 md:px-12 flex flex-col gap-10">
        <h1 class="text-2xl dark:text-gray-200">{intl('Standups')}</h1>
        {!settings.checkinsEnabled && (
          <p class="text-gray-500 dark:text-gray-400">
            {intl('Standups are not enabled for this course.')}
          </p>
        )}
        {settings.checkinsEnabled && isActiveDay && !selectedDay && (
          <TodaysCheckinCard
            courseId={course.id}
            settings={settings}
            localDate={localDate}
            isActiveDay={isActiveDay}
            today={today}
            onSaved={onSaved}
            tracker={tracker}
            trackerHref={trackerHref(URLS.student.course({ course }))}
          />
        )}
        {settings.checkinsEnabled && !isCoach && selectedDay && (
          <TodaysCheckinCard
            key={selectedDay.date}
            courseId={course.id}
            settings={settings}
            localDate={localDate}
            isActiveDay
            date={selectedDay.date}
            today={selectedDay.checkin}
            onSaved={onSaved}
          />
        )}
        {settings.checkinsEnabled && isCoach && (
          <div class="flex flex-col gap-3 items-start border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            <p class="text-gray-700 dark:text-gray-300">
              {intl(
                'Students post their standups on this page. To read them, go to Students → Standups.',
              )}
            </p>
            <BtnPrimary href={`/manage/courses/${course.id}/checkins`}>
              {intl('Open Students → Standups')}
            </BtnPrimary>
          </div>
        )}
        {settings.checkinsEnabled && !isActiveDay && !selectedDay && (
          <p class="text-gray-500 dark:text-gray-400">
            {beforeWindow && (
              <>
                {intl('Standups for this course open on')}{' '}
                {dayjs(settings.startDate).format('dddd, MMM D, YYYY')}.
              </>
            )}
            {afterWindow && (
              <>
                {intl('Standups for this course closed on')}{' '}
                {dayjs(settings.endDate).format('dddd, MMM D, YYYY')}.
              </>
            )}
            {!beforeWindow &&
              !afterWindow &&
              !isStandupDay &&
              (nextDay
                ? intl('There is no standup today. The next one is {day:string}.', {
                    day: dayjs(nextDay).format('dddd, MMM D'),
                  })
                : intl('There is no standup today.'))}
          </p>
        )}
        {settings.checkinsEnabled && !isCoach && openSummary && (
          <WeeklySummaryCard
            courseId={course.id}
            window={openSummary.window}
            localToday={openSummary.localToday}
            current={openSummary.current}
            onSaved={onSummarySaved}
            tracker={tracker?.summaryWeek}
          />
        )}
        {settings.checkinsEnabled && !isCoach && catchUpDays.length > 0 && (
          <section class="flex flex-col gap-3">
            <h2 class="text-sm font-medium text-gray-700 dark:text-gray-300">
              {intl('Missed a day? Catch up here.')}
            </h2>
            <div class="flex flex-wrap gap-2">
              {catchUpDays.map((day) => (
                <CatchUpDayChip
                  key={day.date}
                  day={day}
                  isSelected={selectedDate === day.date}
                  onClick={() => setSelectedDate((d) => (d === day.date ? undefined : day.date))}
                />
              ))}
            </div>
          </section>
        )}
        {!isCoach && (
          <section class="flex flex-col gap-4">
            <h2 class="text-lg font-medium text-gray-900 dark:text-gray-100">
              {intl('Your standups')}
            </h2>
            {checkins.length === 0 && (
              <p class="text-gray-500 dark:text-gray-400">
                {intl('You have not posted a standup yet. Your answers will show up here.')}
              </p>
            )}
            <CheckinHistory checkins={checkins} feedback={feedback} />
          </section>
        )}
        {!isCoach && settings.summariesEnabled && (
          <SummaryHistory summaries={summaries} schedule={settings} feedback={feedback} />
        )}
      </div>
    </StudentPage>
  );
}

async function load(route: RouteLoadProps) {
  const { courseId } = route.params;
  const [course, myCheckins] = await Promise.all([
    rpx.courses.getStudentCourse({ id: courseId }),
    rpx.checkins.getMyCheckins({ courseId }),
  ]);

  return {
    course,
    ...myCheckins,
  };
}
